import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Alert } from '../../components/Alert';
import {
  PlayArrow as TestIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  GetApp as GetAppIcon,
  Security as SecurityIcon,
  Phone as PhoneIcon,
  Speed as SpeedIcon,
  Settings as SettingsIcon,
  Timeline as TimelineIcon,
} from '@material-ui/icons';
import { green, red, orange, blue, grey } from '@material-ui/core/colors';
import { i18n } from '../../translate/i18n';

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      minWidth: 700,
      maxWidth: 900,
      height: '80vh',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  content: {
    padding: theme.spacing(2, 0),
  },
  stepper: {
    backgroundColor: 'transparent',
  },
  stepContent: {
    borderLeft: `1px solid ${theme.palette.divider}`,
    marginLeft: 19,
    paddingLeft: theme.spacing(3),
  },
  testProgress: {
    marginBottom: theme.spacing(2),
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
  },
  testResult: {
    marginTop: theme.spacing(2),
  },
  successResult: {
    color: green[600],
  },
  errorResult: {
    color: red[600],
  },
  warningResult: {
    color: orange[600],
  },
  infoResult: {
    color: blue[600],
  },
  resultItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  resultDetails: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  metricsTable: {
    minWidth: 350,
  },
  metricCell: {
    fontFamily: 'monospace',
    fontSize: '0.875rem',
  },
  statusChip: {
    fontWeight: 600,
    minWidth: 80,
  },
  successChip: {
    backgroundColor: green[100],
    color: green[800],
  },
  errorChip: {
    backgroundColor: red[100],
    color: red[800],
  },
  warningChip: {
    backgroundColor: orange[100],
    color: orange[800],
  },
  pendingChip: {
    backgroundColor: grey[100],
    color: grey[800],
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(2),
  },
  testButton: {
    backgroundColor: blue[500],
    color: 'white',
    '&:hover': {
      backgroundColor: blue[700],
    },
  },
  retryButton: {
    backgroundColor: orange[500],
    color: 'white',
    '&:hover': {
      backgroundColor: orange[700],
    },
  },
  logContainer: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    maxHeight: 200,
    overflow: 'auto',
    padding: theme.spacing(1),
    fontFamily: 'monospace',
    fontSize: '0.75rem',
  },
  logEntry: {
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
  },
  logInfo: {
    backgroundColor: blue[50],
    color: blue[800],
  },
  logSuccess: {
    backgroundColor: green[50],
    color: green[800],
  },
  logWarning: {
    backgroundColor: orange[50],
    color: orange[800],
  },
  logError: {
    backgroundColor: red[50],
    color: red[800],
  },
  summary: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  summaryTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
}));

const TestConnection = ({
  open,
  onClose,
  connection,
  onTest
}) => {
  const classes = useStyles();

  const [activeStep, setActiveStep] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [testResults, setTestResults] = useState([]);
  const [logs, setLogs] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [overallStatus, setOverallStatus] = useState('pending');

  const testSteps = [
    {
      label: i18n.t('asterisk.networkConnectivity'),
      icon: <GetAppIcon />,
      description: i18n.t('asterisk.networkConnectivityDesc'),
    },
    {
      label: i18n.t('asterisk.authentication'),
      icon: <SecurityIcon />,
      description: i18n.t('asterisk.authenticationDesc'),
    },
    {
      label: i18n.t('asterisk.asteriskConnection'),
      icon: <PhoneIcon />,
      description: i18n.t('asterisk.asteriskConnectionDesc'),
    },
    {
      label: i18n.t('asterisk.performanceTest'),
      icon: <SpeedIcon />,
      description: i18n.t('asterisk.performanceTestDesc'),
    },
    {
      label: i18n.t('asterisk.functionalTest'),
      icon: <SettingsIcon />,
      description: i18n.t('asterisk.functionalTestDesc'),
    },
  ];

  useEffect(() => {
    if (open && connection) {
      resetTest();
    }
  }, [open, connection]);

  const resetTest = () => {
    setActiveStep(0);
    setIsRunning(false);
    setProgress(0);
    setTestResults([]);
    setLogs([]);
    setMetrics({});
    setOverallStatus('pending');
  };

  const addLog = (message, type = 'info', timestamp = new Date()) => {
    setLogs(prev => [...prev, {
      message,
      type,
      timestamp: timestamp.toLocaleTimeString()
    }]);
  };

  const updateMetrics = (key, value) => {
    setMetrics(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const addTestResult = (step, status, message, details = null) => {
    setTestResults(prev => [...prev, {
      step,
      status,
      message,
      details,
      timestamp: new Date()
    }]);
  };

  const runTest = async () => {
    setIsRunning(true);
    setProgress(0);
    setActiveStep(0);
    setTestResults([]);
    setLogs([]);
    setMetrics({});

    addLog(`${i18n.t('asterisk.startingTest')} ${connection.name}`, 'info');

    try {
      // Step 1: Network Connectivity
      setActiveStep(0);
      addLog(i18n.t('asterisk.testingNetworkConnectivity'), 'info');
      await runNetworkTest();
      setProgress(20);

      // Step 2: Authentication
      setActiveStep(1);
      addLog(i18n.t('asterisk.testingAuthentication'), 'info');
      await runAuthenticationTest();
      setProgress(40);

      // Step 3: Asterisk Connection
      setActiveStep(2);
      addLog(i18n.t('asterisk.testingAsteriskConnection'), 'info');
      await runAsteriskConnectionTest();
      setProgress(60);

      // Step 4: Performance Test
      setActiveStep(3);
      addLog(i18n.t('asterisk.testingPerformance'), 'info');
      await runPerformanceTest();
      setProgress(80);

      // Step 5: Functional Test
      setActiveStep(4);
      addLog(i18n.t('asterisk.testingFunctionality'), 'info');
      await runFunctionalTest();
      setProgress(100);

      // Determine overall status
      const hasErrors = testResults.some(result => result.status === 'error');
      const hasWarnings = testResults.some(result => result.status === 'warning');

      if (hasErrors) {
        setOverallStatus('error');
        addLog(i18n.t('asterisk.testCompletedWithErrors'), 'error');
      } else if (hasWarnings) {
        setOverallStatus('warning');
        addLog(i18n.t('asterisk.testCompletedWithWarnings'), 'warning');
      } else {
        setOverallStatus('success');
        addLog(i18n.t('asterisk.testCompletedSuccessfully'), 'success');
      }

    } catch (error) {
      setOverallStatus('error');
      addLog(`${i18n.t('asterisk.testFailed')}: ${error.message}`, 'error');
      addTestResult(activeStep, 'error', i18n.t('asterisk.testFailed'), error.message);
    } finally {
      setIsRunning(false);
    }
  };

  const runNetworkTest = async () => {
    const startTime = Date.now();

    try {
      // Simulate network connectivity test
      await new Promise(resolve => setTimeout(resolve, 1000));

      const responseTime = Date.now() - startTime;
      updateMetrics('responseTime', responseTime);
      updateMetrics('host', connection.host);
      updateMetrics('port', connection.port);

      if (responseTime > 5000) {
        addTestResult(0, 'warning', i18n.t('asterisk.slowNetworkResponse'), `${responseTime}ms`);
        addLog(`${i18n.t('asterisk.slowResponse')}: ${responseTime}ms`, 'warning');
      } else {
        addTestResult(0, 'success', i18n.t('asterisk.networkConnectivitySuccess'), `${responseTime}ms`);
        addLog(`${i18n.t('asterisk.networkConnected')}: ${responseTime}ms`, 'success');
      }
    } catch (error) {
      addTestResult(0, 'error', i18n.t('asterisk.networkConnectivityFailed'), error.message);
      addLog(`${i18n.t('asterisk.networkError')}: ${error.message}`, 'error');
      throw error;
    }
  };

  const runAuthenticationTest = async () => {
    try {
      // Simulate authentication test
      await new Promise(resolve => setTimeout(resolve, 1500));

      updateMetrics('username', connection.username);
      updateMetrics('authMethod', connection.secure ? 'Secure' : 'Basic');

      addTestResult(1, 'success', i18n.t('asterisk.authenticationSuccess'));
      addLog(i18n.t('asterisk.authenticationSuccessful'), 'success');
    } catch (error) {
      addTestResult(1, 'error', i18n.t('asterisk.authenticationFailed'), error.message);
      addLog(`${i18n.t('asterisk.authenticationError')}: ${error.message}`, 'error');
      throw error;
    }
  };

  const runAsteriskConnectionTest = async () => {
    try {
      // Simulate Asterisk ARI connection test
      await new Promise(resolve => setTimeout(resolve, 2000));

      if (onTest) {
        const result = await onTest(connection);
        if (result.success) {
          updateMetrics('ariVersion', result.version || '18.0.0');
          updateMetrics('status', 'Connected');
          addTestResult(2, 'success', i18n.t('asterisk.asteriskConnectionSuccess'));
          addLog(i18n.t('asterisk.ariConnectionEstablished'), 'success');
        } else {
          throw new Error(result.message);
        }
      } else {
        // Fallback simulation
        updateMetrics('ariVersion', '18.0.0');
        updateMetrics('status', 'Connected');
        addTestResult(2, 'success', i18n.t('asterisk.asteriskConnectionSuccess'));
        addLog(i18n.t('asterisk.ariConnectionEstablished'), 'success');
      }
    } catch (error) {
      addTestResult(2, 'error', i18n.t('asterisk.asteriskConnectionFailed'), error.message);
      addLog(`${i18n.t('asterisk.ariConnectionError')}: ${error.message}`, 'error');
      throw error;
    }
  };

  const runPerformanceTest = async () => {
    try {
      // Simulate performance test
      await new Promise(resolve => setTimeout(resolve, 1500));

      const latency = Math.floor(Math.random() * 50) + 10; // 10-60ms
      const throughput = Math.floor(Math.random() * 100) + 50; // 50-150 Mbps

      updateMetrics('latency', `${latency}ms`);
      updateMetrics('throughput', `${throughput} Mbps`);

      if (latency > 100) {
        addTestResult(3, 'warning', i18n.t('asterisk.highLatencyDetected'), `${latency}ms`);
        addLog(`${i18n.t('asterisk.highLatency')}: ${latency}ms`, 'warning');
      } else {
        addTestResult(3, 'success', i18n.t('asterisk.performanceTestSuccess'));
        addLog(`${i18n.t('asterisk.performanceGood')}: ${latency}ms latency`, 'success');
      }
    } catch (error) {
      addTestResult(3, 'error', i18n.t('asterisk.performanceTestFailed'), error.message);
      addLog(`${i18n.t('asterisk.performanceError')}: ${error.message}`, 'error');
      throw error;
    }
  };

  const runFunctionalTest = async () => {
    try {
      // Simulate functional test
      await new Promise(resolve => setTimeout(resolve, 1000));

      updateMetrics('endpoints', '5 active');
      updateMetrics('channels', '0 active');
      updateMetrics('bridges', '0 active');

      addTestResult(4, 'success', i18n.t('asterisk.functionalTestSuccess'));
      addLog(i18n.t('asterisk.allFunctionsWorking'), 'success');
    } catch (error) {
      addTestResult(4, 'error', i18n.t('asterisk.functionalTestFailed'), error.message);
      addLog(`${i18n.t('asterisk.functionalError')}: ${error.message}`, 'error');
      throw error;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className={classes.successResult} />;
      case 'error':
        return <ErrorIcon className={classes.errorResult} />;
      case 'warning':
        return <WarningIcon className={classes.warningResult} />;
      default:
        return <InfoIcon className={classes.infoResult} />;
    }
  };

  const getStatusChip = (status) => {
    const statusMap = {
      success: { label: i18n.t('asterisk.passed'), className: classes.successChip },
      error: { label: i18n.t('asterisk.failed'), className: classes.errorChip },
      warning: { label: i18n.t('asterisk.warning'), className: classes.warningChip },
      pending: { label: i18n.t('asterisk.pending'), className: classes.pendingChip },
    };

    const config = statusMap[status] || statusMap.pending;

    return (
      <Chip
        size="small"
        label={config.label}
        className={`${classes.statusChip} ${config.className}`}
      />
    );
  };

  const getLogEntryClass = (type) => {
    switch (type) {
      case 'success':
        return `${classes.logEntry} ${classes.logSuccess}`;
      case 'error':
        return `${classes.logEntry} ${classes.logError}`;
      case 'warning':
        return `${classes.logEntry} ${classes.logWarning}`;
      default:
        return `${classes.logEntry} ${classes.logInfo}`;
    }
  };

  if (!connection) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className={classes.dialog}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box className={classes.header}>
          <TestIcon color="primary" />
          <Typography variant="h6">
            {i18n.t('asterisk.testConnection')} - {connection.name}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            style={{ marginLeft: 'auto' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.content}>
          {/* Progress Bar */}
          {isRunning && (
            <Box className={classes.testProgress}>
              <Typography variant="body2" gutterBottom>
                {i18n.t('asterisk.testProgress')}: {Math.round(progress)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={progress}
                className={classes.progressBar}
              />
            </Box>
          )}

          {/* Test Steps */}
          <Stepper activeStep={activeStep} orientation="vertical" className={classes.stepper}>
            {testSteps.map((step, index) => {
              const stepResult = testResults.find(result => result.step === index);

              return (
                <Step key={step.label}>
                  <StepLabel
                    icon={stepResult ? getStatusIcon(stepResult.status) : step.icon}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {step.label}
                      {stepResult && getStatusChip(stepResult.status)}
                    </Box>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="textSecondary">
                      {step.description}
                    </Typography>
                    {stepResult && (
                      <Box className={classes.testResult}>
                        <Alert severity={stepResult.status}>
                          {stepResult.message}
                          {stepResult.details && (
                            <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                              {stepResult.details}
                            </Typography>
                          )}
                        </Alert>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              );
            })}
          </Stepper>

          {/* Results Summary */}
          {testResults.length > 0 && (
            <Paper className={classes.summary}>
              <Typography className={classes.summaryTitle}>
                <TimelineIcon />
                {i18n.t('asterisk.testSummary')}
              </Typography>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {i18n.t('asterisk.connectionMetrics')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer>
                    <Table className={classes.metricsTable} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{i18n.t('asterisk.metric')}</TableCell>
                          <TableCell>{i18n.t('asterisk.value')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(metrics).map(([key, value]) => (
                          <TableRow key={key}>
                            <TableCell>{key}</TableCell>
                            <TableCell className={classes.metricCell}>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">
                    {i18n.t('asterisk.testLogs')}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box className={classes.logContainer}>
                    {logs.map((log, index) => (
                      <Box key={index} className={getLogEntryClass(log.type)}>
                        <strong>{log.timestamp}</strong> - {log.message}
                      </Box>
                    ))}
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Paper>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>
          {i18n.t('asterisk.close')}
        </Button>

        {testResults.length > 0 && !isRunning && (
          <Button
            className={classes.retryButton}
            onClick={runTest}
            startIcon={<RefreshIcon />}
          >
            {i18n.t('asterisk.retryTest')}
          </Button>
        )}

        <Button
          className={classes.testButton}
          onClick={runTest}
          disabled={isRunning}
          startIcon={<TestIcon />}
        >
          {isRunning ? i18n.t('asterisk.running') : i18n.t('asterisk.startTest')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestConnection;